import { Parameter, InteractiveConfig } from '@shared/types';
import WorkspaceFile from '../lib/WorkspaceFile.ts';

// Credit
// https://github.com/seasick/openscad-web-gui/blob/main/src/worker/types.ts

export const enum WorkerMessageType {
  PREVIEW = 'preview',
  EXPORT = 'export',
  FS_READ = 'fs.read',
  FS_WRITE = 'fs.write',
  FS_UNLINK = 'fs.unlink',
  FS_WRITE_BATCH = 'fs.writeBatch',
  FS_CLEAR = 'fs.clear',
  PROGRESS = 'progress',
  PARTIAL_RESULT = 'partial_result',
  /** Compile a single color pass — used by the worker pool */
  GENERATE_COLOR_STL = 'generate_color_stl',
  /** Compile the ghost (%) geometry pass — used by the worker pool */
  GENERATE_GHOST_STL = 'generate_ghost_stl',
  /** Run color detection only, returning detected colors + lights */
  DETECT_COLORS = 'detect_colors',
}

// Represents a file to be written to the virtual filesystem
export interface VirtualFile {
  path: string;
  content: string | ArrayBuffer;
  isBinary?: boolean;
}

// Batch write data for multiple files
export type BatchWriteWorkerMessageData = {
  files: VirtualFile[];
};

/**
 * A pre-parsed SVG fragment file synthesized on the main thread for
 * `import("file.svg#id")` / `import("file.svg[N]")` calls.
 *
 * The main thread does the DOMParser-based extraction (DOMParser is not
 * universally available in WorkerGlobalScope) and ships these files to
 * the worker per-compile-job.  The worker writes them into its file list
 * before WASM init and removes them afterwards.
 */
export type SvgFragmentFile = {
  /** Virtual FS path under `/__svg_frag__/`, e.g. "/__svg_frag__/shapes__star.svg". */
  path: string;
  /** Standalone SVG text content. */
  content: string;
};

export type WorkerProgressData = {
  progress: number; // 0-100
  message: string;
};

/** Payload for a single-color STL generation job dispatched to a pool worker */
export type GenerateColorSTLMessageData = {
  code: string;
  params: Parameter[];
  targetColor: [number, number, number, number];
  isGhost: boolean;
  targetMaterial?: MaterialProperties;
  targetExcludedFromExport: boolean;
  /**
   * Pre-parsed SVG fragment files (id-form / index-form imports).  When set,
   * the worker injects these into its file list before WASM init.  See
   * `src/utils/svgFragmentPrepare.ts` for how they are produced.
   */
  svgFragmentFiles?: SvgFragmentFile[];
};

/** Payload for a ghost STL generation job dispatched to a pool worker */
export type GenerateGhostSTLMessageData = {
  code: string;
  params: Parameter[];
  /** Pre-parsed SVG fragment files — see GenerateColorSTLMessageData. */
  svgFragmentFiles?: SvgFragmentFile[];
};

/** Payload for a color-detection-only job dispatched to a pool worker */
export type DetectColorsMessageData = {
  code: string;
  params: Parameter[];
};

/** Response from a GENERATE_COLOR_STL or GENERATE_GHOST_STL job */
export type GenerateSTLResponseData = {
  stlData: Uint8Array | null;
};

/**
 * A single grid object defined by a scene_grid() call in OpenSCAD source.
 * type: 0 = plane (flat grid lines), 1 = cube wireframe, 2 = sphere wireframe.
 */
export type SceneGrid = {
  type: 0 | 1 | 2;
  size: number;
  position: [number, number, number];
  rotation: [number, number, number]; // euler angles in degrees (X, Y, Z)
  color: [number, number, number]; // RGB 0-1
  opacity: number; // 0-1
  cellSize: number; // minor grid cell spacing (plane only)
  sectionSize: number; // major grid section spacing (plane only)
  cellColor: [number, number, number] | undefined; // undefined = inherit from color
  sectionColor: [number, number, number] | undefined; // undefined = inherit from color (slightly brighter)
  infinite: boolean; // plane only: extend infinitely like default grid
  fadeDistance: number; // fade-out distance for infinite grids
};

/**
 * Per-field overrides detected from scene_* module calls in OpenSCAD source.
 * Only fields explicitly passed by the user are populated; undefined means
 * "not set in code — defer to the UI value".
 * Boolean SCAD params are encoded as 0/1 numbers and decoded to boolean here.
 */
export type SceneOverrides = {
  material?: {
    metalness?: number;
    roughness?: number;
    envMapIntensity?: number;
    flatShading?: boolean;
    usePhysicalMaterial?: boolean;
    clearcoat?: number;
    clearcoatRoughness?: number;
    sheen?: number;
    sheenRoughness?: number;
    sheenColor?: [number, number, number];
    iridescence?: number;
    iridescenceIOR?: number;
    transmission?: number;
    ior?: number;
    thickness?: number;
    attenuationColor?: [number, number, number];
    attenuationDistance?: number;
  };
  defaultLights?: {
    ambient?: number;
    key?: number;
    fill?: number;
    rim?: number;
  };
  shadows?: {
    enabled?: boolean;
    soft?: boolean;
    intensity?: number;
    mapSize?: number;
  };
  bloom?: {
    enabled?: boolean;
    intensity?: number;
    luminanceThreshold?: number;
    luminanceSmoothing?: number;
    mipmapBlur?: boolean;
  };
  ssao?: {
    enabled?: boolean;
    intensity?: number;
    aoRadius?: number;
    samples?: number;
    luminanceInfluence?: number;
  };
  /** Background color as a CSS hex string, e.g. "#1a1a2e". */
  backgroundColor?: string;
  /**
   * Code-defined grid objects. Each scene_grid() call appends one entry.
   * When this array is non-empty the default UI grid is suppressed, but the
   * grid toggle still controls visibility of all grids (default + code-defined).
   */
  grids?: SceneGrid[];
  /**
   * Initial camera state set by scene_camera(). Applied once when the
   * model loads — does not lock the camera from being orbited afterwards.
   */
  camera?: {
    position?: [number, number, number];
    target?: [number, number, number];
    fov?: number;
    type?: 'perspective' | 'orthographic';
  };
};

/**
 * A single js_svg() call instance detected during the detection pass.
 *
 * Each unique (filename, value, props) combination found in the SCAD echo
 * output becomes one entry. The OpenSCAD worker generates one SVG file per
 * entry and writes it to the WASM virtual filesystem before compilation.
 */
export type JsSvgInstance = {
  /** The JS script filename (value of the "filename" key in props=[]) */
  filename: string;
  /**
   * The per-instance discriminator (value of the "value" key in props=[]).
   * Used to produce distinct SVGs for each loop iteration.
   * May be a number, string, boolean, or undef (null).
   */
  value: unknown;
  /** All props from props=[], including filename and value plus any extras */
  props: Record<string, unknown>;
};

/** Response from a DETECT_COLORS job */
export type DetectColorsResponseData = {
  colors: {
    color: [number, number, number, number];
    isGhost: boolean;
    excludedFromExport: boolean;
    material?: MaterialProperties;
    /** Texture scripts/images linked to this geometry, keyed by channel name */
    textureScripts?: Array<{
      filename: string;
      channel: string;
      /** "script" = JS canvas worker, "image" = static image (SVG/PNG/JPG/WebP) rasterization */
      type: 'script' | 'image';
      /** Explicit rasterization width (image only; undefined = infer from source) */
      width?: number;
      /** Explicit rasterization height (image only; undefined = infer from source) */
      height?: number;
      /** Per-instance key/value pairs passed from OpenSCAD as the third argument to texture_script() */
      props?: Record<string, unknown>;
      /** Optional face constraint: restricts texture to faces pointing in this direction.
       *  One of: "top", "bottom", "front", "back", "left", "right".
       *  Omit (or empty string) to apply texture to all faces. */
      face?: string;
    }>;
    /** Set when this geometry was wrapped with interactive() in SCAD source. */
    interactive?: InteractiveConfig;
    /** Set when this geometry was wrapped with annotation() in SCAD source. */
    annotation?: AnnotationDef;
    /** Set when this geometry was wrapped with motion() in SCAD source. */
    motion?: MotionDef;
    /** Set when this geometry was wrapped with clone_ref() in SCAD source. */
    cloneKey?: string;
  }[];
  lights: SceneLight[];
  pipCameras: PiPCamera[];
  /**
   * Ordered intro camera path defined by camera_path() calls.
   * Plays once after the first compile; empty array means no intro path.
   */
  cameraPath: CameraPathWaypoint[];
  sceneOverrides: SceneOverrides;
  /** All clone() placements declared in the SCAD source. */
  clones: CloneDef[];
  /**
   * All js_svg() instances detected in the SCAD source, deduplicated by
   * (filename, value). The OpenSCAD worker generates one SVG per entry.
   */
  jsSvgInstances?: JsSvgInstance[];
};

type WorkerMessageDataMap = {
  [WorkerMessageType.PREVIEW]: OpenSCADWorkerMessageData;
  [WorkerMessageType.EXPORT]: OpenSCADWorkerMessageData;
  [WorkerMessageType.FS_READ]: FileSystemWorkerMessageData;
  [WorkerMessageType.FS_WRITE]: FileSystemWorkerMessageData;
  [WorkerMessageType.FS_UNLINK]: FileSystemWorkerMessageData;
  [WorkerMessageType.FS_WRITE_BATCH]: BatchWriteWorkerMessageData;
  [WorkerMessageType.FS_CLEAR]: Record<string, never>;
  [WorkerMessageType.PROGRESS]: WorkerProgressData;
  [WorkerMessageType.PARTIAL_RESULT]: PartialColorResult;
  [WorkerMessageType.GENERATE_COLOR_STL]: GenerateColorSTLMessageData;
  [WorkerMessageType.GENERATE_GHOST_STL]: GenerateGhostSTLMessageData;
  [WorkerMessageType.DETECT_COLORS]: DetectColorsMessageData;
};

export type WorkerMessage = {
  id?: string | number;
  type: WorkerMessageType;
  data: WorkerMessageDataMap[WorkerMessageType];
};

/** Serialized form of OpenSCADError as sent across the worker message boundary */
export type SerializedOpenSCADError = {
  name: 'OpenSCADError';
  message: string;
  code: string;
  stdErr: string[];
  stdOut: string[];
};

export type WorkerResponseMessage = {
  id: string | number;
  type: WorkerMessageType;
  data:
    | OpenSCADWorkerResponseData
    | FileSystemWorkerMessageData
    | boolean
    | WorkerProgressData
    | PartialColorResult
    | GenerateSTLResponseData
    | DetectColorsResponseData
    | null;
  err?: Error | SerializedOpenSCADError;
};

export type OpenSCADWorkerMessageData = {
  code: string;
  fileType: string;
  params: Parameter[];
  /** Unique identifier generated by the main thread for this compile run */
  compileId?: string;
  /**
   * Pre-parsed SVG fragment files for `import("file.svg#id")` /
   * `import("file.svg[N]")` calls.  Produced on the main thread and
   * injected into the worker's file list before compilation.
   */
  svgFragmentFiles?: SvgFragmentFile[];
};

export type MaterialProperties = {
  metalness: number; // -1 = use global default
  roughness: number; // -1 = use global default
  emissive: [number, number, number]; // [-1,-1,-1] = none/default
  emissiveIntensity: number; // -1 = use global default
  // Physical material properties (MeshPhysicalMaterial-only)
  clearcoat: number; // -1 = use global default, 0-1
  clearcoatRoughness: number; // -1 = use global default, 0-1
  sheen: number; // -1 = use global default, 0-1
  sheenRoughness: number; // -1 = use global default, 0-1
  sheenColor: [number, number, number]; // [-1,-1,-1] = none/default
  iridescence: number; // -1 = use global default, 0-1
  iridescenceIOR: number; // -1 = use global default, 1-2.333
  transmission: number; // -1 = use global default, 0-1
  ior: number; // -1 = use global default, 1-2.333
  thickness: number; // -1 = use global default, 0+
  attenuationColor: [number, number, number]; // [-1,-1,-1] = none/default
  attenuationDistance: number; // -1 = use global default, 0+
};

export type AnnotationDef = {
  name: string;
  description: string;
  alwaysVisible: boolean;
};

export type ColoredSTL = {
  color: [number, number, number, number]; // RGBA values 0-1
  data: Uint8Array;
  isGhost?: boolean; // True for % modifier - preview only, exclude from export
  excludedFromExport?: boolean; // True for exclude_from_export() - show in preview (toggleable), exclude from export
  material?: MaterialProperties; // Per-object PBR material properties
  /** Annotation metadata set when this geometry was wrapped with annotation() in SCAD source. */
  annotation?: AnnotationDef;
  /** Texture scripts/images linked to this geometry via texture_script()/texture_svg()/texture_png() in SCAD code */
  textureScripts?: Array<{
    filename: string;
    channel: string;
    /** "script" = JS canvas worker, "image" = static image (SVG/PNG/JPG/WebP) rasterization */
    type: 'script' | 'image';
    /** Explicit rasterization width (image only; undefined = infer from source) */
    width?: number;
    /** Explicit rasterization height (image only; undefined = infer from source) */
    height?: number;
    /** Optional face constraint — restricts texture to faces pointing in this direction */
    face?: string;
  }>;
  /** Set when this geometry was wrapped with interactive() in SCAD source. */
  interactive?: InteractiveConfig;
  /** Set when this geometry was wrapped with motion() in SCAD source. */
  motion?: MotionDef;
  /** Set when this geometry was wrapped with clone_ref() in SCAD source. */
  cloneKey?: string;
  /** Set when this geometry was wrapped with shader_material("file.js") in SCAD source. */
  shaderScript?: string;
};

/** A single color's compiled STL streamed from the worker before the full compile finishes */
export type PartialColorResult = {
  coloredSTL: ColoredSTL;
  /** Identifies the compile run this result belongs to; used to discard stale results */
  compileId: string;
};

// ---------------------------------------------------------------------------
// LFO types
// ---------------------------------------------------------------------------

/**
 * Waveform shapes for LFO modulation.
 *
 * - sine:      Smooth 0→1→0 oscillation (cosine-based, starts at 0)
 * - triangle:  Linear ramp up then down, 0→1→0 per cycle
 * - square:    Hard off/on — 0 for first half-cycle, 1 for second half
 * - sawtooth:  Linear ramp 0→1 per cycle, then instant reset
 */
export type LFOShape = 'sine' | 'triangle' | 'square' | 'sawtooth';

/**
 * A single-channel LFO that modulates a scalar light property.
 *
 * The LFO output is in the absolute range [min, max], independent of the
 * light's base value. For example, `min=0, max=1` with `lfo_intensity`
 * means the intensity oscillates between 0 and 1 regardless of what
 * `intensity` is set to in the SCAD code.
 *
 * Inline string encoding: `"shape:freq:phase:min:max"`
 * Example: `"square:2:180:0:1"` — square wave at 2 Hz, 180° offset, range 0–1
 */
export type LightLFO = {
  /** Waveform shape. */
  shape: LFOShape;
  /** Oscillation frequency in Hz (cycles per second). */
  frequency: number;
  /** Phase offset in degrees (0–360). Shifts the start point of the cycle. */
  phase: number;
  /** Absolute output minimum. */
  min: number;
  /** Absolute output maximum. */
  max: number;
};

/**
 * A whole-color LFO that oscillates between two RGB colors.
 *
 * The LFO waveform (0→1) is used to lerp from `colorA` to `colorB`.
 * Takes precedence over per-channel `colorR/G/B` bindings when present.
 *
 * Inline string encoding: `"shape:freq:phase:[r1,g1,b1]:[r2,g2,b2]"`
 * Example: `"sine:0.25:0:[1,0.1,0]:[0.1,0.2,1]"` — 4-second red↔blue cycle
 */
export type LightColorLFO = {
  /** Waveform shape. */
  shape: LFOShape;
  /** Oscillation frequency in Hz. */
  frequency: number;
  /** Phase offset in degrees (0–360). */
  phase: number;
  /** Color at LFO output = 0 (start of cycle). */
  colorA: [number, number, number];
  /** Color at LFO output = 1 (peak of cycle). */
  colorB: [number, number, number];
};

/**
 * LFO bindings for a light source.
 *
 * Each field, if present, modulates the corresponding light property in the
 * Three.js render loop without triggering a WASM recompile.
 *
 * `color` (whole-color) takes precedence over `colorR/G/B` per-channel
 * bindings when both are present.
 *
 * `angle`, `penumbra`, and `rotation` are only meaningful on SpotLights.
 * `rotation` animates the gobo map texture rotation (degrees); it has no
 * visible effect when no `map` is set.
 */
export type LightLFOBindings = {
  /** Modulates light intensity. Range is absolute (min..max). */
  intensity?: LightLFO;
  /** Modulates the red channel of the light color. */
  colorR?: LightLFO;
  /** Modulates the green channel of the light color. */
  colorG?: LightLFO;
  /** Modulates the blue channel of the light color. */
  colorB?: LightLFO;
  /** Oscillates the full light color between two RGB values. Overrides colorR/G/B. */
  color?: LightColorLFO;
  /** Modulates the spot cone angle in degrees. SpotLight only. */
  angle?: LightLFO;
  /** Modulates the spot penumbra (0–1). SpotLight only. */
  penumbra?: LightLFO;
  /** Modulates the gobo map rotation in degrees. SpotLight only. */
  rotation?: LightLFO;
};

// ---------------------------------------------------------------------------
// Clone types
// ---------------------------------------------------------------------------

/**
 * A single clone placement defined by a clone() call in OpenSCAD source.
 *
 * The key references a geometry that was tagged with clone_ref() in SCAD.
 * Position, rotation (Euler XYZ degrees), and scale are applied in Three.js
 * at render time — no WASM recompile is triggered.
 */
export type CloneDef = {
  /** Key that matches the clone_ref() wrapper on the source geometry. */
  key: string;
  /** XYZ position in OpenSCAD coordinate space. */
  position: [number, number, number];
  /** Euler rotation in degrees, applied as XYZ intrinsic rotations. */
  rotation: [number, number, number];
  /** Per-axis scale factors. */
  scale: [number, number, number];
};

// ---------------------------------------------------------------------------
// Motion types
// ---------------------------------------------------------------------------

/**
 * Motion definition for a geometry wrapped with motion() in OpenSCAD source.
 *
 * All four motion layers are independent and additive. Any layer with default
 * values (0 / empty LFO) is a no-op, so you can combine any subset freely.
 *
 * Layers applied each frame (in order):
 *   1. Axis translation — oscillate along a world-space direction vector
 *   2. Orbit           — revolve around a fixed world-space center point
 *   3. Spin            — continuous rotation around the object's local axis
 *   4. Rock            — oscillating rotation (back-and-forth) around a local axis
 *
 * LFO strings reuse the same `"shape:freq:phase:min:max"` encoding as lights.
 */
export type MotionDef = {
  // ---------------------------------------------------------------------------
  // Axis translation (LFO-driven oscillation along a direction)
  // ---------------------------------------------------------------------------
  /** World-space direction to oscillate along. Normalized on the JS side. */
  translateAxis: [number, number, number];
  /**
   * LFO that drives the translation distance (in scene units).
   * Output range is the displacement along translateAxis:
   *   min=0, max=20 → oscillates 0..20 units in front of the base position
   *   min=-10, max=10 → symmetric oscillation around the base position
   */
  translateLFO?: LightLFO;

  // ---------------------------------------------------------------------------
  // Orbital path (continuous revolution around a world-space center)
  // ---------------------------------------------------------------------------
  /** World-space center point of the orbit. The object revolves around this. */
  orbitCenter: [number, number, number];
  /**
   * Normal vector of the orbit plane (right-hand rule).
   * e.g. [0,0,1] → orbit in the XY plane (default, revolves around Z axis).
   */
  orbitAxis: [number, number, number];
  /** Primary (semi-major) radius in scene units. 0 = orbit disabled. */
  orbitRadius: number;
  /** Secondary (semi-minor) radius for an ellipse. 0 = use orbitRadius → circle. */
  orbitRadiusB: number;
  /** Revolutions per second. Positive = counter-clockwise when viewed along orbitAxis. */
  orbitSpeed: number;
  /** Initial orbit angle offset in degrees. */
  orbitOffset: number;

  // ---------------------------------------------------------------------------
  // Continuous spin (rotation around the object's local center)
  // ---------------------------------------------------------------------------
  /** Local axis to spin around. e.g. [0,0,1] = spin around Z. Normalized on JS side. */
  rotateAxis: [number, number, number];
  /** Full rotations per second. 0 = no spin. */
  rotateSpeed: number;
  /** Initial angle offset in degrees applied before animation starts. */
  rotateOffset: number;

  // ---------------------------------------------------------------------------
  // Oscillating rock (LFO-driven rotation around a local axis)
  // ---------------------------------------------------------------------------
  /** Local axis to rock around. e.g. [1,0,0] = rock around X. Normalized on JS side. */
  rockAxis: [number, number, number];
  /**
   * LFO that drives the rock angle (in degrees).
   * min=-30, max=30 → rocks ±30° symmetrically.
   */
  rockLFO?: LightLFO;
};

// ---------------------------------------------------------------------------

/**
 * Orbit definition for an animated light source.
 *
 * The light's static `position` is the anchor point at orbit angle 0.
 * The orbit is an ellipse in the plane defined by `tilt` (Euler angles
 * in degrees, applied as XYZ intrinsic rotations). `radiusB = 0` means
 * a perfect circle using `radius` for both axes.
 *
 * Over time the orbit plane itself precesses: it rotates around
 * `driftAxis` at `driftSpeed` degrees per second.
 */
export type LightOrbit = {
  /** Primary (major) radius of the orbit ellipse. */
  radius: number;
  /** Secondary (minor) radius; 0 = use `radius` → circle. */
  radiusB: number;
  /** Euler angles [rx, ry, rz] in degrees to orient the orbit plane. */
  tilt: [number, number, number];
  /** Revolutions per second the light travels along the orbit. */
  speed: number;
  /** Degrees per second the orbit plane rotates (precession). */
  driftSpeed: number;
  /** World-space axis the orbit plane drifts around. */
  driftAxis: [number, number, number];
};

export type LightAmbient = {
  type: 'ambient';
  color: [number, number, number];
  intensity: number;
  lfo?: LightLFOBindings;
};

export type LightDirectional = {
  type: 'directional';
  color: [number, number, number];
  position: [number, number, number];
  intensity: number;
  orbit?: LightOrbit;
  lfo?: LightLFOBindings;
};

export type LightPoint = {
  type: 'point';
  color: [number, number, number];
  position: [number, number, number];
  intensity: number;
  distance: number;
  decay: number;
  orbit?: LightOrbit;
  lfo?: LightLFOBindings;
};

export type LightSpot = {
  type: 'spot';
  color: [number, number, number];
  position: [number, number, number];
  intensity: number;
  distance: number;
  angle: number;
  penumbra: number;
  decay: number;
  /** Filename of an SVG or PNG project file to use as a gobo (light projection map). */
  map?: string;
  orbit?: LightOrbit;
  lfo?: LightLFOBindings;
};

export type SceneLight =
  | LightAmbient
  | LightDirectional
  | LightPoint
  | LightSpot;

/**
 * A single transition effect applied when a PiP panel is clicked.
 *
 * Position-path effects (highest priority wins; rest modify):
 * - "direct"   — smooth lerp directly to the destination (default)
 * - "orbit"    — cinematic orbit around the target before arriving
 * - "zoom_out" — pull back away from the scene, then zoom into destination
 * - "arc"      — parabolic lift above the straight-line path mid-flight
 *
 * Orientation effects (additive, independent of position):
 * - "roll"     — camera rolls around the view axis
 * - "tilt"     — camera pitches up/down with a sin envelope (peaks at midpoint)
 * - "shake"    — additive positional vibration, enveloped by shake_phase
 *
 * FOV effect (independent, perspective cameras only):
 * - "bounce"   — decaying overshoot oscillation on the final approach
 * - "fov_pulse"— field of view narrows or widens mid-flight then returns
 *
 * Multiple effects can be combined, e.g. `["orbit", "roll", "fov_pulse"]`.
 * Composition rules:
 *   - orbit + zoom_out: zoom_out modulates orbit radius (sin envelope)
 *   - orbit + arc:      arc height added to orbit's midpoint height
 *   - orbit + bounce:   bounce oscillates the orbit radius on final approach
 *   - roll/tilt/shake:  always additive on top of any position driver
 *   - fov_pulse:        always independent
 */
export type PiPTransitionType =
  | 'direct'
  | 'orbit'
  | 'zoom_out'
  | 'arc'
  | 'roll'
  | 'tilt'
  | 'shake'
  | 'bounce'
  | 'fov_pulse';

/**
 * Bitmask constants used by the WASM echo encoder to pack the transitions
 * array into a single integer that OpenSCAD can echo.
 *
 * Bit 0 (1) = orbit
 * Bit 1 (2) = zoom_out
 * Bit 2 (4) = roll
 * 0          = direct (no bits set)
 */
export const TRANSITION_BIT_ORBIT = 1;
export const TRANSITION_BIT_ZOOM_OUT = 2;
export const TRANSITION_BIT_ROLL = 4;

/** Decode a bitmask integer back to a `PiPTransitionType[]`. */
export function decodeTransitionBitmask(bits: number): PiPTransitionType[] {
  // 0 means "direct" — no composable effects
  if (bits === 0) return ['direct'];
  const result: PiPTransitionType[] = [];
  if (bits & TRANSITION_BIT_ORBIT) result.push('orbit');
  if (bits & TRANSITION_BIT_ZOOM_OUT) result.push('zoom_out');
  if (bits & TRANSITION_BIT_ROLL) result.push('roll');
  return result;
}

/**
 * Easing curve applied to the transition progress (t: 0→1).
 *
 * - "exponential" — frame-rate-independent exponential decay (default, matches legacy feel)
 * - "linear"      — constant speed
 * - "ease_in"     — starts slow, accelerates
 * - "ease_out"    — starts fast, decelerates
 * - "ease_in_out" — slow at both ends, fast in the middle
 */
export type PiPEasingType =
  | 'exponential'
  | 'linear'
  | 'ease_in'
  | 'ease_out'
  | 'ease_in_out';

/**
 * A picture-in-picture camera overlay defined by a camera_pip() call in
 * OpenSCAD source. The camera has a fixed position and look-at target that
 * are unaffected by the main camera's orbit controls.
 *
 * Overlay position and size use normalized viewport coordinates (0–1):
 *   x=0, y=0 is the top-left corner of the viewer.
 *   width=0.3 means 30% of the viewer width.
 *
 * Up to MAX_PIP_CAMERAS cameras are supported; extras are silently dropped.
 */
export type PiPCamera = {
  /** Camera position in 3D scene units */
  position: [number, number, number];
  /** Look-at target point in 3D scene units */
  target: [number, number, number];
  /** Projection type */
  type: 'perspective' | 'orthographic';
  /** Field of view in degrees (perspective) or zoom level (orthographic) */
  fov: number;
  /** Overlay X position (0–1, measured from left edge) */
  x: number;
  /** Overlay Y position (0–1, measured from top edge) */
  y: number;
  /** Overlay width as fraction of viewer width (0–1) */
  width: number;
  /** Overlay height as fraction of viewer height (0–1) */
  height: number;
  /**
   * Fixed pixel X position. Defined when the source value |x| > 1.
   * Positive = pixels from left; negative = pixels from right edge.
   * When set, takes precedence over the fractional `x` for CSS rendering.
   */
  xPx?: number;
  /**
   * Fixed pixel Y position. Defined when the source value |y| > 1.
   * Positive = pixels from top; negative = pixels from bottom edge.
   * When set, takes precedence over the fractional `y` for CSS rendering.
   */
  yPx?: number;
  /**
   * Fixed pixel width. Defined when the source value width > 1.
   * When set, takes precedence over the fractional `width` for CSS rendering.
   */
  widthPx?: number;
  /**
   * Fixed pixel height. Defined when the source value height > 1.
   * When set, takes precedence over the fractional `height` for CSS rendering.
   */
  heightPx?: number;
  /** Optional text label shown at the bottom of the overlay */
  label: string;

  // ── Transition / animation ──────────────────────────────────────────────────

  /**
   * One or more animation effects to play in parallel when this PiP is clicked.
   * - A single `["direct"]` gives the default straight lerp.
   * - Multiple effects run simultaneously, e.g. `["orbit", "roll"]`.
   * - `["orbit", "zoom_out"]` orbits while modulating the radius (zoom_out
   *   inflates the orbit path at the midpoint and contracts it at the end).
   */
  transitions: PiPTransitionType[];
  /** Easing curve applied to the animation progress. Default: "exponential" */
  easing: PiPEasingType;
  /** Total animation duration in seconds. Default: 1.0 */
  duration: number;

  // orbit options
  /** How many full orbits to complete before arriving (orbit transition only). Default: 1.0 */
  orbitTurns: number;
  /**
   * Axis to orbit around (orbit transition only).
   * "auto" resolves to the world Y axis.
   * Default: "auto"
   */
  orbitAxis: 'auto' | 'x' | 'y' | 'z';

  // zoom_out options
  /**
   * How far to pull the camera back from the scene centre, as a multiplier of
   * the average camera-to-target distance. Default: 2.5
   */
  zoomOutFactor: number;

  // roll options
  /** Total degrees to roll the camera around the view axis during the transition. Default: 360 */
  rollAngle: number;

  // arc options
  /**
   * Height (in scene units) to lift the camera above the straight-line midpoint
   * during the arc. Negative values dip below. Default: 30
   */
  arcHeight: number;

  // bounce options
  /**
   * Peak overshoot as a fraction of remaining distance to destination. Default: 0.3
   * e.g. 0.3 = 30% overshoot before springing back.
   */
  bounceAmplitude: number;
  /** Number of oscillations during the bounce. Default: 2.0 */
  bounceFrequency: number;

  // shake options
  /** Amplitude of the positional shake in scene units. Default: 2.0 */
  shakeIntensity: number;
  /** Oscillation frequency of the shake in cycles per second. Default: 8.0 */
  shakeFrequency: number;
  /**
   * Which part of the transition is shaken.
   * "start" — fades from full at t=0 to zero at t=0.5
   * "end"   — zero at t=0, builds toward t=1 then snaps clean (default)
   * "both"  — sin envelope, peaks at t=0.5, zero at both ends
   */
  shakePhase: 'start' | 'end' | 'both';

  // tilt options
  /**
   * Peak pitch angle in degrees. Positive = tilts up (camera looks upward at midpoint),
   * negative = tilts down. Uses a sin envelope so it returns to level at the destination.
   * Default: 30
   */
  tiltAngle: number;

  // fov_pulse options
  /** Degrees by which the FOV deviates from the destination FOV at the midpoint. Default: 20 */
  fovPulseAmount: number;
  /**
   * Direction of the FOV pulse.
   * "out" — FOV widens at midpoint (expansive, revealing). Default.
   * "in"  — FOV narrows at midpoint (telephoto punch-in, claustrophobic).
   */
  fovPulseDirection: 'in' | 'out';

  /**
   * How much randomness to apply to the animation on each trigger (0–1).
   * 0 = fully deterministic (default).
   * 1 = maximum jitter — every active effect's parameters are perturbed by
   *     up to their full randomizable range, so each click feels distinct.
   * Values in between scale the perturbations proportionally.
   */
  randomness: number;
};

/** Maximum number of PiP cameras supported (to stay within WebGL context limits) */
export const MAX_PIP_CAMERAS = 4;

/**
 * A single waypoint in the intro camera path defined by a camera_path() call
 * in OpenSCAD source. Waypoints play in source order, once, after the first
 * compile. The last waypoint is where the user starts interacting.
 *
 * Shares all transition/animation parameters with PiPCamera but has no
 * overlay layout fields (no x/y/width/height/label — it's not a PiP panel).
 */
export type CameraPathWaypoint = {
  /** Camera position in 3D scene units */
  position: [number, number, number];
  /** Look-at target point in 3D scene units */
  target: [number, number, number];
  /** Projection type */
  type: 'perspective' | 'orthographic';
  /** Field of view in degrees (perspective) or zoom level (orthographic) */
  fov: number;

  // ── Transition / animation ──────────────────────────────────────────────────

  /** One or more animation effects to play in parallel for this waypoint. */
  transitions: PiPTransitionType[];
  /** Easing curve applied to the animation progress. Default: "exponential" */
  easing: PiPEasingType;
  /** Total animation duration in seconds. Default: 1.0 */
  duration: number;

  // orbit options
  /** How many full orbits to complete before arriving (orbit transition only). Default: 1.0 */
  orbitTurns: number;
  /** Axis to orbit around. Default: "auto" */
  orbitAxis: 'auto' | 'x' | 'y' | 'z';

  // zoom_out options
  /** Pull-back radius multiplier. Default: 2.5 */
  zoomOutFactor: number;

  // roll options
  /** Total degrees to roll during the transition. Default: 360 */
  rollAngle: number;

  // arc options
  /** Height above straight-line midpoint in scene units. Default: 30 */
  arcHeight: number;

  // bounce options
  /** Peak overshoot as a fraction of remaining distance. Default: 0.3 */
  bounceAmplitude: number;
  /** Number of oscillations during the bounce. Default: 2.0 */
  bounceFrequency: number;

  // shake options
  /** Amplitude of the positional shake in scene units. Default: 2.0 */
  shakeIntensity: number;
  /** Oscillation frequency of the shake in cycles per second. Default: 8.0 */
  shakeFrequency: number;
  /** Which part of the transition is shaken. Default: "end" */
  shakePhase: 'start' | 'end' | 'both';

  // tilt options
  /** Peak pitch angle in degrees. Default: 30 */
  tiltAngle: number;

  // fov_pulse options
  /** Degrees by which the FOV deviates from the destination FOV at the midpoint. Default: 20 */
  fovPulseAmount: number;
  /** Direction of the FOV pulse. Default: "out" */
  fovPulseDirection: 'in' | 'out';

  /** Per-trigger randomness scale (0–1). Default: 0 */
  randomness: number;
};

/**
 * An SVG file rendered as a 2-D HTML overlay on top of the viewer.
 * Defined by a svg_overlay() call in OpenSCAD source.
 * Produces no 3-D geometry — purely a viewport decoration.
 *
 * Position and size follow the same convention as PiPCamera:
 *   Fraction mode:  |value| ≤ 1  (0 = left/top edge, 1 = right/bottom edge)
 *   Pixel mode:     |value| > 1  (positive = from left/top, negative = from right/bottom)
 *
 * height = 0 means "derive from the SVG's natural aspect ratio".
 * When both width and height are non-zero the SVG is stretched to fit.
 */
export type SvgOverlay = {
  /** Filename of the SVG in the project files list */
  file: string;
  /** Overlay X position (raw source value — fraction or pixel when |v| > 1) */
  x: number;
  /** Overlay Y position (raw source value — fraction or pixel when |v| > 1) */
  y: number;
  /** Overlay width (raw source value — fraction or pixel when v > 1) */
  width: number;
  /**
   * Overlay height (raw source value).
   * 0 = derive from the SVG's natural aspect ratio at render time.
   * Non-zero = explicit value (fraction or pixel).
   */
  height: number;
  /** Pixel X when |x| > 1 (positive = from left, negative = from right) */
  xPx?: number;
  /** Pixel Y when |y| > 1 (positive = from top, negative = from bottom) */
  yPx?: number;
  /** Pixel width when width > 1 */
  widthPx?: number;
  /** Pixel height when height > 1. Undefined when height = 0 (AR-computed). */
  heightPx?: number;
  /**
   * Optional CSS color string to inject as the CSS `color` property on the
   * overlay wrapper. SVG elements that use `currentColor` will inherit it.
   * Empty string = no override.
   */
  color: string;
  /** Overall opacity of the overlay (0–1). Default 1.0. */
  opacity: number;
};

export type OpenSCADWorkerResponseData = {
  log: {
    stdErr: string[];
    stdOut: string[];
  };
  fileType: string;
  output: Uint8Array;
  exitCode: number;
  duration: number;
  // Multi-color support
  coloredOutputs?: ColoredSTL[];
  lights?: SceneLight[];
  sceneOverrides?: SceneOverrides;
  /** Clone placements declared via clone() in the SCAD source. */
  clones?: CloneDef[];
  /** True when the result was served entirely from the in-worker compilation cache */
  cacheHit?: boolean;
  /** Echo of the compileId from the request, used to discard stale responses */
  compileId?: string;
};

export type FileSystemWorkerMessageData = {
  path: string;
  content?: WorkspaceFile; // Content is only necessary when writing
};
